import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';
import Select from 'react-select';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import API helper
import httprequester from '../../lib/httprequester';

const AddWebinar = ({ history }) => {
  // Status options for react-select
  const statusOptions = [
    { value: 'UPCOMING', label: 'Upcoming' },
    { value: 'ONGOING', label: 'Ongoing' },
    { value: 'COMPLETED', label: 'Completed' }
  ];

  // State management
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    desc: '',
    date: '',
    quota: '',
    status: 'UPCOMING',
    Skp: '',
    Point: '',
    live_url: '',
    video_url: '',
    link_lms: '',
    thumbnail: ''
  });
  const [specialists, setSpecialists] = useState([]);
  const [selectedSpecialists, setSelectedSpecialists] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingSpecialists, setFetchingSpecialists] = useState(true);
  const [fetchingSpeakers, setFetchingSpeakers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Speaker creation modal state
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [newSpeaker, setNewSpeaker] = useState({ name: '' });
  const [creatingSpeaker, setCreatingSpeaker] = useState(false);

  // Fetch specialists and speakers on component mount
  useEffect(() => {
    fetchSpecialists();
    fetchSpeakers();
  }, []);

  // Fetch specialists from API
  const fetchSpecialists = async () => {
    setFetchingSpecialists(true);
    try {
      const response = await httprequester.getAsync('webinar/cms/specialists');
      if (response.data.data) {
        setSpecialists(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching specialists:', err);
    } finally {
      setFetchingSpecialists(false);
    }
  };

  // Fetch speakers from API
  const fetchSpeakers = async () => {
    setFetchingSpeakers(true);
    try {
      const response = await httprequester.getAsync('webinar/cms/speakers');
      if (response.data.data) {
        setSpeakers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching speakers:', err);
    } finally {
      setFetchingSpeakers(false);
    }
  };

  // Create new speaker
  const createSpeaker = async () => {
    if (!newSpeaker.name.trim()) {
      setError('Speaker name is required');
      return;
    }

    setCreatingSpeaker(true);
    try {
      const response = await httprequester.postAsync('webinar/cms/speakers', {
        name: newSpeaker.name.trim()
      });

      if (response.data && response.data.message) {
        setSuccess(response.data.message);
        setNewSpeaker({ name: '' });
        setShowSpeakerModal(false);
        // Fetch speakers again to include the new one
        await fetchSpeakers();
      } else {
        setError('Failed to create speaker');
      }
    } catch (err) {
      console.error('Error creating speaker:', err);
      setError('Failed to create speaker. Please try again.');
    } finally {
      setCreatingSpeaker(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate slug from title
  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');

    setFormData(prev => ({
      ...prev,
      slug: slug
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.title || !formData.slug) {
        setError('Title and Slug are required fields');
        setLoading(false);
        return;
      }

      if (selectedSpecialists.length === 0) {
        setError('At least one specialist must be selected');
        setLoading(false);
        return;
      }

      if (selectedSpeakers.length === 0) {
        setError('At least one speaker must be selected');
        setLoading(false);
        return;
      }

      // Create JSON payload for submission
      const submitData = {
        title: formData.title,
        slug: formData.slug,
        desc: formData.desc,
        date: formData.date || null,
        quota: formData.quota ? parseInt(formData.quota) : null,
        status: formData.status,
        Skp: formData.Skp ? parseFloat(formData.Skp) : 0,
        Point: formData.Point ? parseFloat(formData.Point) : 0,
        live_url: formData.live_url || null,
        video_url: formData.video_url || null,
        link_lms: formData.link_lms || null,
        thumbnail: formData.thumbnail || null,
        created_by: JSON.parse(localStorage.getItem('user'))?.admin_name || 'Unknown',
        specialists: selectedSpecialists.map(specialist => ({
          specialist_id: specialist.value,
          specialist_name: specialist.label
        })),
        speakers: selectedSpeakers.map(speaker => ({
          speaker_id: speaker.value,
          speaker_name: speaker.label
        }))
      };

      const response = await httprequester.postAsync('webinar/cms/webinars', submitData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.message) {
        setSuccess(response.data.message || 'Webinar created successfully!');
        setTimeout(() => {
          history.push('/cme-online');
        }, 2000);
      } else {
        setError(response.data?.message || 'Failed to create webinar');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Missing required fields. Please check your input.');
      } else if (err.response?.status === 409) {
        setError('Slug already exists. Please choose a different one.');
      } else {
        setError('Error creating webinar');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    history.push('/cme-online');
  };

  // Clear alerts
  const clearAlert = () => setError('');
  const clearSuccess = () => setSuccess('');

  // PropTypes
  AddWebinar.propTypes = {
    history: PropTypes.object.isRequired
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Add New Webinar | CME Online | Ruang Ilmiah CMS</title>
        </MetaTags>

        <div className="container-fluid">
          <Breadcrumbs
            title="CME Online"
            breadcrumbItem="Add New Webinar"
          />

          {/* Header Actions */}
          <div className="row mb-3">
            <div className="col">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Add New Webinar</h4>
                <button className="btn btn-secondary" onClick={handleBack}>
                  <i className="bx bx-arrow-back me-1"></i>
                  Back to List
                </button>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Left Column - Form */}
              <div className="col-7">
                {/* Basic Information */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Basic Information</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="title" className="form-label">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          id="title"
                          name="title"
                          type="text"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Enter webinar title"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="slug" className="form-label">
                          Slug <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            id="slug"
                            name="slug"
                            type="text"
                            className="form-control"
                            value={formData.slug}
                            onChange={handleInputChange}
                            placeholder="webinar-slug"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={generateSlug}
                            disabled={!formData.title}
                          >
                            Generate
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="desc" className="form-label">Description</label>
                    <textarea
                      id="desc"
                      name="desc"
                      className="form-control"
                      rows="4"
                      value={formData.desc}
                      onChange={handleInputChange}
                      placeholder="Enter webinar description"
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label">Event Date & Time</label>
                        <input
                          id="date"
                          name="date"
                          type="datetime-local"
                          className="form-control"
                          value={formData.date}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <Select
                          options={statusOptions}
                          value={statusOptions.find(option => option.value === formData.status)}
                          onChange={(selectedOption) => setFormData(prev => ({
                            ...prev,
                            status: selectedOption ? selectedOption.value : 'UPCOMING'
                          }))}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                              boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                              '&:hover': {
                                borderColor: '#80bdff'
                              }
                            }),
                            singleValue: (provided, state) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            placeholder: (provided, state) => ({
                              ...provided,
                              color: '#6c757d'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isFocused ? '#e9ecef' : 'white',
                              color: '#495057',
                              '&:active': {
                                backgroundColor: '#e9ecef'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="quota" className="form-label">Quota</label>
                        <input
                          id="quota"
                          name="quota"
                          type="number"
                          className="form-control"
                          value={formData.quota}
                          onChange={handleInputChange}
                          placeholder="Enter participant quota"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="thumbnail" className="form-label">Thumbnail URL</label>
                        <input
                          id="thumbnail"
                          name="thumbnail"
                          type="url"
                          className="form-control"
                          value={formData.thumbnail}
                          onChange={handleInputChange}
                          placeholder="https://example.com/image.jpg"
                        />
                        <small className="text-muted">
                          Enter the URL of the uploaded thumbnail image
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CME Points */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">CME Points</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="Skp" className="form-label">SKP Points</label>
                        <input
                          id="Skp"
                          name="Skp"
                          type="number"
                          className="form-control"
                          value={formData.Skp}
                          onChange={handleInputChange}
                          placeholder="Enter SKP points"
                          min="0"
                          step="0.5"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="Point" className="form-label">CME Points</label>
                        <input
                          id="Point"
                          name="Point"
                          type="number"
                          className="form-control"
                          value={formData.Point}
                          onChange={handleInputChange}
                          placeholder="Enter CME points"
                          min="0"
                          step="0.5"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* URLs */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Links & URLs</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="live_url" className="form-label">Live Meeting URL</label>
                        <input
                          id="live_url"
                          name="live_url"
                          type="url"
                          className="form-control"
                          value={formData.live_url}
                          onChange={handleInputChange}
                          placeholder="https://www.youtube.com/live/your-live-id"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="video_url" className="form-label">Video Recording URL</label>
                        <input
                          id="video_url"
                          name="video_url"
                          type="url"
                          className="form-control"
                          value={formData.video_url}
                          onChange={handleInputChange}
                          placeholder="https://www.youtube.com/watch?v=your-video-id"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label htmlFor="link_lms" className="form-label">Link LMS</label>
                        <input
                          id="link_lms"
                          name="link_lms"
                          type="url"
                          className="form-control"
                          value={formData.link_lms}
                          onChange={handleInputChange}
                          placeholder="https://lms.kemkes.go.id/"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Note about Speakers & Specialists */}
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Specialists & Speakers</h5>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="specialists" className="form-label">
                          Specialists <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={specialists.map(specialist => ({
                            value: specialist.specialist_id,
                            label: specialist.specialist_name
                          }))}
                          value={selectedSpecialists}
                          onChange={setSelectedSpecialists}
                          placeholder="Select specialists..."
                          isLoading={fetchingSpecialists}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                              boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                              '&:hover': {
                                borderColor: '#80bdff'
                              }
                            }),
                            multiValue: (provided) => ({
                              ...provided,
                              backgroundColor: '#e9ecef'
                            }),
                            multiValueLabel: (provided) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            multiValueRemove: (provided) => ({
                              ...provided,
                              color: '#6c757d',
                              '&:hover': {
                                backgroundColor: '#dc3545',
                                color: 'white'
                              }
                            })
                          }}
                        />
                        <small className="text-muted">
                          Select one or more specialists for this webinar
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="speakers" className="form-label">
                          Speakers <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <Select
                              isMulti
                              options={speakers.map(speaker => ({
                                value: speaker.speaker_id,
                                label: speaker.name
                              }))}
                              value={selectedSpeakers}
                              onChange={setSelectedSpeakers}
                              placeholder="Select speakers..."
                              isLoading={fetchingSpeakers}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
                                  boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                                  '&:hover': {
                                    borderColor: '#80bdff'
                                  }
                                }),
                                multiValue: (provided) => ({
                                  ...provided,
                                  backgroundColor: '#e9ecef'
                                }),
                                multiValueLabel: (provided) => ({
                                  ...provided,
                                  color: '#495057'
                                }),
                                multiValueRemove: (provided) => ({
                                  ...provided,
                                  color: '#6c757d',
                                  '&:hover': {
                                    backgroundColor: '#dc3545',
                                    color: 'white'
                                  }
                                })
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => setShowSpeakerModal(true)}
                            title="Add new speaker"
                          >
                            <i className="bx bx-user-plus"></i>
                          </button>
                        </div>
                        <small className="text-muted">
                          Select one or more speakers for this webinar
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="col-5">
                <div className="card shadow-sm rounded-4 p-4 mb-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Form Preview</h5>

                  <div className="mb-3">
                    <small className="text-muted">Title</small>
                    <p className="mb-0 fw-semibold fs-5">{formData.title || 'No title entered'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Slug</small>
                    <p className="mb-0 text-muted">{formData.slug || 'No slug generated'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Status</small>
                    <div>
                      <span className={`badge bg-${formData.status === 'UPCOMING' ? 'warning' :
                        formData.status === 'ONGOING' ? 'success' : 'secondary'
                        }`}>
                        {formData.status}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Event Date</small>
                    <p className="mb-0">
                      {formData.date ? new Date(formData.date).toLocaleString('id-ID') : 'Not set'}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Quota</small>
                    <p className="mb-0">{formData.quota || 'Unlimited'}</p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">CME Points</small>
                    <p className="mb-0">
                      SKP: {formData.Skp || 0} | CME: {formData.Point || 0}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Thumbnail</small>
                    <p className="mb-0">
                      {formData.thumbnail ? (
                        <span className="text-success">URL provided</span>
                      ) : (
                        <span className="text-muted">No thumbnail URL</span>
                      )}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Specialists</small>
                    <p className="mb-0">
                      {selectedSpecialists.length > 0 ? (
                        <div>
                          {selectedSpecialists.map((specialist, index) => (
                            <span key={specialist.value} className="badge bg-success me-1">
                              {specialist.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No specialists selected</span>
                      )}
                    </p>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Speakers</small>
                    <p className="mb-0">
                      {selectedSpeakers.length > 0 ? (
                        <div>
                          {selectedSpeakers.map((speaker, index) => (
                            <span key={speaker.value} className="badge bg-info me-1">
                              {speaker.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No speakers selected</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="card shadow-sm rounded-4 p-4">
                  <h5 className="fw-bold mb-3 border-bottom pb-2">Actions</h5>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={loading || !formData.title || !formData.slug || selectedSpecialists.length === 0 || selectedSpeakers.length === 0}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save me-1"></i>
                          Create Webinar
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={handleBack}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <SweetAlert
          danger
          title="Error!"
          onConfirm={clearAlert}
        >
          {error}
        </SweetAlert>
      )}

      {/* Success Alert */}
      {success && (
        <SweetAlert
          success
          title="Success!"
          onConfirm={clearSuccess}
        >
          {success}
        </SweetAlert>
      )}

      {/* Speaker Creation Modal */}
      {showSpeakerModal && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 1055 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Speaker</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSpeakerModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="speakerName" className="form-label">
                    Speaker Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="speakerName"
                    value={newSpeaker.name}
                    onChange={(e) => setNewSpeaker({ name: e.target.value })}
                    placeholder="Enter speaker name"
                    disabled={creatingSpeaker}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSpeakerModal(false)}
                  disabled={creatingSpeaker}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createSpeaker}
                  disabled={creatingSpeaker || !newSpeaker.name.trim()}
                >
                  {creatingSpeaker ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Speaker'
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
        </div>
      )}
    </React.Fragment>
  );
};

export default withRouter(AddWebinar);
